import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldPath, FieldValues, PathValue, useFormContext, UseFormReturn, useWatch } from 'react-hook-form';
import { FormFieldWithLabelFactory } from '../../../../../components/Form/FormFieldAndLabelFactory';
import { ICaptableDataModel } from '../../../../../data-models/captable2.data-model';
import { roundToPrecision } from '../../../../../util/math-utils';
import { partialSalesFields } from '../../forms/scenarioFields';
import { useClearValueOnChange } from './partialBreakdownUtils';
import { IPartialScenario } from './PartialSaleFields';
import { PartialSaleType } from './PartialSaleToggle';
import { getScenarioCaptableData } from './scenario-schema-utils';

interface IPartialBreakdownElemProps {
  captable: ICaptableDataModel;
  transactionIndex: number;
  breakdownIndex: number;
  type: PartialSaleType;
}
export const PartialBreakdownElem = memo(function PartialBreakdownElem({
  captable,
  transactionIndex,
  breakdownIndex,
  type,
}: IPartialBreakdownElemProps) {
  const { fundOptionsByShareClassId, shareClassIdToClientInvestmentsByFund, shareClassesById } = useMemo(
    () => getScenarioCaptableData(captable),
    [captable]
  );

  const methods = useFormContext<IPartialScenario>();

  const fieldPath =
    `scenarioTransactions.${transactionIndex}.partialExitBreakdown.${breakdownIndex}` as const;

  const captableInvestmentIdValue = useWatch({
    control: methods.control,
    name: `${fieldPath}.captableInvestmentId`,
  });
  const fundIdValue = useWatch({ control: methods.control, name: `${fieldPath}.fundId` });

  const maxShares: number | null = useMemo(() => {
    if (!captableInvestmentIdValue) {
      return null;
    }

    if (type === PartialSaleType.aggregated || !fundIdValue) {
      return shareClassesById.get(captableInvestmentIdValue ?? -1)?.clientFullyDilutedShares ?? null;
    } else {
      return (
        shareClassIdToClientInvestmentsByFund.get(captableInvestmentIdValue)?.get(fundIdValue!)
          ?.fullyDilutedShares ?? null
      );
    }
  }, [captableInvestmentIdValue, fundIdValue, shareClassIdToClientInvestmentsByFund, shareClassesById, type]);

  const calculatePercentage = useCallback(
    (noOfShares?: number | null) => {
      if (typeof noOfShares !== 'number' || !maxShares) {
        return null;
      }
      return roundToPrecision((noOfShares / maxShares) * 100, 2);
    },
    [maxShares]
  );
  const calculateShares = useCallback(
    (percentage?: number | null) => {
      if (typeof percentage !== 'number' || !maxShares) {
        return null;
      }
      return roundToPrecision((percentage / 100) * maxShares, 2);
    },
    [maxShares]
  );

  const triggersRef = useRef(
    new Set<FieldPath<IPartialScenario>>([
      `${fieldPath}.captableInvestmentId` as const,
      `${fieldPath}.fundId` as const,
    ])
  );
  const subscriptionsRef = useRef(
    new Set<FieldPath<IPartialScenario>>([
      `${fieldPath}.numberOfShares`,
      `${fieldPath}.numberOfSharesPercentage`,
    ])
  );
  const resetCount = useClearValueOnChange({
    triggerFieldPaths: triggersRef.current,
    subscriptionFieldPaths: subscriptionsRef.current,
    methods,
  });

  useCalculatedField({
    methods,
    fieldPath: `${fieldPath}.numberOfShares`,
    calcFunc: calculateShares,
    depPath: `${fieldPath}.numberOfSharesPercentage`,
    reverseCalcFunc: calculatePercentage,
  });

  useCalculatedField({
    methods,
    fieldPath: `${fieldPath}.numberOfSharesPercentage`,
    calcFunc: calculatePercentage,
    depPath: `${fieldPath}.numberOfShares`,
    reverseCalcFunc: calculateShares,
  });

  const { captableInvestmentId, numberOfShares, numberOfSharesPercentage, fundId } = partialSalesFields({
    captable,
    transactionIndex,
    breakdownIndex,
    fundOptionsByShareClassId,
    captableInvestmentId: captableInvestmentIdValue,
  });

  return (
    <>
      <FormFieldWithLabelFactory formField={captableInvestmentId} />
      {type === PartialSaleType.byFund && <FormFieldWithLabelFactory formField={fundId} />}
      <FormFieldWithLabelFactory formField={numberOfShares} key={`numberOfShares-${resetCount}`} />
      <FormFieldWithLabelFactory
        formField={numberOfSharesPercentage}
        key={`numberOfSharesPercentage-${resetCount}`}
      />
    </>
  );
});

interface ISyncFieldsParams<T extends FieldValues> {
  methods: UseFormReturn<T>;
  fieldPath: FieldPath<T>;
  calcFunc: (value?: number | null) => PathValue<T, FieldPath<T>>;
  depPath: FieldPath<T>;
  reverseCalcFunc: (value?: number | null) => PathValue<T, FieldPath<T>>;
}

export function useCalculatedField<T extends FieldValues>({
  methods,
  fieldPath: fieldPath,
  calcFunc: calcFunc,
  depPath,
  reverseCalcFunc,
}: ISyncFieldsParams<T>) {
  const { setValue, getValues } = methods;
  const depValue = useWatch({ control: methods.control, name: depPath });

  useEffect(() => {
    const calculatedValue = calcFunc(depValue);
    const currentValue = getValues(fieldPath);
    const calcStr = calculatedValue == null ? 'null' : calculatedValue.toFixed(2);
    const currStr = currentValue == null ? 'null' : currentValue.toFixed(2);

    const reverseCalcRes = reverseCalcFunc(currentValue);
    const reverseCalcResStr = reverseCalcRes == null ? 'null' : reverseCalcRes.toFixed(2);
    const depStr = depValue == null ? 'null' : depValue.toFixed(2);

    // we need both checks to prevent multiple field updates on single change event caused by floating point precision issues
    if (calcStr !== currStr && reverseCalcResStr !== depStr) {
      setValue(fieldPath, calculatedValue);
    }
  }, [depValue, fieldPath, calcFunc, setValue, getValues, reverseCalcFunc]);
}
